import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useUploadComplianceDocument } from "@/hooks/use-compliance";
import { apiFetch } from "@/lib/api";

interface DocumentType {
  id: string;
  key: string;
  label: string;
  requiresExpiry: boolean;
  isActive: boolean;
}

interface UploadComplianceDialogProps {
  companyId: string;
  open: boolean;
  onClose: () => void;
}

export function UploadComplianceDialog({ companyId, open, onClose }: UploadComplianceDialogProps) {
  const uploadMutation = useUploadComplianceDocument();
  const docTypesQuery = useQuery<{ data: DocumentType[] }>({
    queryKey: ["compliance", "document-types"],
    queryFn: () => apiFetch<{ data: DocumentType[] }>("/compliance/document-types"),
    enabled: open,
  });

  const [documentTypeId, setDocumentTypeId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [issueDate, setIssueDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [notes, setNotes] = useState("");

  const docTypes = (docTypesQuery.data?.data ?? []).filter((d) => d.isActive);
  const selectedDocType = docTypes.find((d) => d.id === documentTypeId) ?? null;
  const requiresExpiry = selectedDocType?.requiresExpiry ?? false;

  const reset = () => {
    setDocumentTypeId("");
    setFile(null);
    setIssueDate("");
    setExpiryDate("");
    setNotes("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const canSubmit =
    !!documentTypeId && !!file && (!requiresExpiry || !!expiryDate) && !uploadMutation.isPending;

  const handleSubmit = async () => {
    if (!documentTypeId || !file) return;
    try {
      await uploadMutation.mutateAsync({
        companyId,
        documentTypeId,
        file,
        issueDate: issueDate || null,
        expiryDate: expiryDate || null,
        notes: notes || null,
      });
      handleClose();
    } catch {
      // toast surfaced by the mutation
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Upload Compliance Document</DialogTitle>
          <DialogDescription>
            Upload a new compliance document for your company. Existing documents of the same type
            will be archived as a prior version.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Document Type</Label>
            <Select value={documentTypeId} onValueChange={setDocumentTypeId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a document type" />
              </SelectTrigger>
              <SelectContent>
                {docTypes.map((dt) => (
                  <SelectItem key={dt.id} value={dt.id}>
                    {dt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="compliance-file">File</Label>
            <Input
              id="compliance-file"
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
            />
            {file ? (
              <p className="text-xs text-muted-foreground">
                {file.name} · {(file.size / 1024).toFixed(0)} KB
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Issue Date</Label>
              <DatePicker
                value={issueDate}
                onChange={(v) => setIssueDate(v ?? "")}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-2">
              <Label>
                Expiry Date
                {requiresExpiry ? <span className="ml-1 text-destructive">*</span> : null}
              </Label>
              <DatePicker
                value={expiryDate}
                onChange={(v) => setExpiryDate(v ?? "")}
                placeholder={requiresExpiry ? "Required" : "Optional"}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="compliance-notes">Notes</Label>
            <Textarea
              id="compliance-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional context for reviewers"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={uploadMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {uploadMutation.isPending ? "Uploading..." : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
